import { useCallback, useEffect, useRef, useState } from "react";
import RemergeApi from "../http/RemergeApi";
import { buildProvisionedUser, shouldProvisionAuth0User } from "./provision";
import { ProvisionedUser } from "./types";

type UseAuth0ProvisionParams = {
  isLoading: boolean;
  isAuth0Authenticated: boolean;
  hasUser: boolean;
  remergeApi: RemergeApi;
  auth0User?: {
    email?: string;
    name?: string;
  };
  onProvisionError: () => void;
};

/**
 * Shared provisioning hook for Auth0-authenticated users without legacy cookie state.
 */
export function useAuth0Provision({
  isLoading,
  isAuth0Authenticated,
  hasUser,
  remergeApi,
  auth0User,
  onProvisionError,
}: UseAuth0ProvisionParams): {
  isProvisioning: boolean;
  provisionedUser: ProvisionedUser | undefined;
  clearProvisionedUser: () => void;
  resetProvisionAttempt: () => void;
} {
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [provisionedUser, setProvisionedUser] = useState<ProvisionedUser | undefined>(undefined);
  const hasAttemptedProvision = useRef(false);

  useEffect(() => {
    if (
      !shouldProvisionAuth0User({
        isLoading,
        isAuth0Authenticated,
        hasUser,
        hasAttemptedProvision: hasAttemptedProvision.current,
      })
    ) {
      return;
    }

    hasAttemptedProvision.current = true;
    setIsProvisioning(true);

    void provisionUser();

    async function provisionUser() {
      try {
        const { id } = await remergeApi.auth0Provision();
        const resolvedId = String(id);
        // usersShow is best-effort enrichment for role/org fields. We still keep
        // the provisioned session when this call fails to avoid auth loops.
        let usersShowResponse;
        try {
          usersShowResponse = await remergeApi.usersShow(resolvedId);
        } catch {
          usersShowResponse = undefined;
        }
        const result = buildProvisionedUser({
          id: resolvedId,
          auth0User,
          userResource: usersShowResponse?.data,
        });

        setProvisionedUser(result);
      } catch {
        onProvisionError();
      } finally {
        setIsProvisioning(false);
      }
    }
  }, [auth0User, hasUser, isAuth0Authenticated, isLoading, onProvisionError, remergeApi]);

  const resetProvisionAttempt = useCallback(() => {
    hasAttemptedProvision.current = false;
  }, []);

  const clearProvisionedUser = useCallback(() => {
    setProvisionedUser(undefined);
  }, []);

  return {
    isProvisioning,
    provisionedUser,
    clearProvisionedUser,
    resetProvisionAttempt,
  };
}
